import { useEffect, useState } from "react";

interface SharePanelProps {
  open: boolean;
  title: string;
  onClose: () => void;
}

export default function SharePanel({ open, title, onClose }: SharePanelProps) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) setCopied(false);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const url = typeof window !== "undefined" ? window.location.href : "";
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2200);
    } catch {
      const input = document.createElement("textarea");
      input.value = url;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2200);
    }
  };

  const socials = [
    {
      label: "微博",
      icon: "ri-weibo-line",
      href: `https://service.weibo.com/share/share.php?url=${encodedUrl}&title=${encodedTitle}`,
    },
    {
      label: "X",
      icon: "ri-twitter-x-line",
      href: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
    },
    {
      label: "QQ",
      icon: "ri-qq-line",
      href: `https://connect.qq.com/widget/shareqq/index.html?url=${encodedUrl}&title=${encodedTitle}`,
    },
  ];

  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center px-4 pb-4 sm:items-center sm:pb-0">
      <button
        type="button"
        aria-label="关闭分享面板"
        onClick={onClose}
        className="absolute inset-0 cursor-pointer bg-foreground-950/55 backdrop-blur-sm"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="分享"
        className="relative z-10 w-full max-w-md rounded-lg border border-background-200 bg-background-50 p-6"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-heading text-lg font-semibold text-foreground-950">分享给朋友</h3>
            <p className="mt-1 line-clamp-1 text-xs text-foreground-500">{title}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="关闭"
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-background-200"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="mt-5 flex items-center gap-3">
          <div className="flex min-w-0 flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-100 px-3 py-2">
            <span className="flex h-4 w-4 shrink-0 items-center justify-center text-foreground-500">
              <i className="ri-link-m" />
            </span>
            <span className="truncate text-xs text-foreground-600">{url}</span>
          </div>
          <button
            type="button"
            onClick={copyLink}
            className="shrink-0 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            {copied ? "已复制" : "复制链接"}
          </button>
        </div>

        <div className="mt-6 grid grid-cols-3 gap-3">
          {socials.map((social) => (
            <a
              key={social.label}
              href={social.href}
              target="_blank"
              rel="noopener noreferrer"
              className="flex cursor-pointer flex-col items-center gap-2 rounded-lg border border-background-200 bg-background-100 px-2 py-4 text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              <span className="flex h-8 w-8 items-center justify-center">
                <i className={`${social.icon} text-xl`} />
              </span>
              <span className="text-xs">{social.label}</span>
            </a>
          ))}
        </div>

        <p className="mt-5 text-center text-[11px] text-foreground-500">
          微信用户可复制链接后粘贴给好友
        </p>
      </div>
    </div>
  );
}